import { useEffect, useMemo, useState } from 'react';
import { Tooltip, capitalize } from '@mui/material';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { Box, DatePicker, ListHeader, ListRow, dataModelIcons } from '/src/components';
import { useDateStore, useTransactionStore } from '/src/context';
import { mediumDate } from '/src/lib/constants';
import { Loan } from '/src/lib/models';
import { DataModelPluralType, DataModelType } from '/src/lib/types';
import { currency } from '/src/utils';

export type TransactionsLedgerProps = {
  /**
   * Loan to display transactions for
   */
  loan?: Loan;
  /**
   * Whether to show the date picker component
   */
  showDatePicker?: boolean;
  /**
   * Whether to show the ledger's header
   */
  showHeader?: boolean;
  /**
   * Whether to hide "list is empty" or "no Loan selected" messages
   */
  hideErrors?: boolean;
  /**
   * Number of list items to show before scrolling
   */
  numItemsBeforeScroll?: number;
};

export const TransactionsLedger: React.FC<TransactionsLedgerProps> = observer(
  ({ loan, showDatePicker = true, showHeader = true, hideErrors = false, numItemsBeforeScroll = 8 }) => {
    const headerHeight = 89;
    const rowHeight = 48;
    const listHeight = headerHeight + rowHeight * numItemsBeforeScroll + 'px';

    /** Context **/
    const transactionStore = useTransactionStore();
    const dateStore = useDateStore();

    /** State **/
    const [sortType, setSortType] = useState('sequenceNumber');
    const [sortReverse, setSortReverse] = useState(false);

    /** Computed **/
    const transactions = transactionStore.transactions;
    const totals = transactionStore.totals;
    const dateRange = dateStore.dateRange;

    const listData = useMemo(() => {
      if (transactions) {
        const list = transactions
          .filter((transaction) => transaction.__t !== DataModelPluralType.Adjustment)
          .toSorted((a, b) => {
            const _a = (a as any)[sortType] ?? 0;
            const _b = (b as any)[sortType] ?? 0;
            return _a - _b;
          });
        if (sortReverse) list.reverse();

        return list;
      }
    }, [transactions, sortType, sortReverse]);

    /** Methods **/

    const sortList = (type?: string) => {
      if (type) {
        if (sortType === type) setSortReverse(!sortReverse);
        else setSortType(type);
      }
    };

    /** Effects **/

    useEffect(() => {
      if (loan?._id && dateRange?.start && dateRange?.end) {
        transactionStore.query({
          loanId: loan?._id,
          start: dateRange.start.toISOString(), // TODO: do we convert to ISO or UTC string?
          end: dateRange.end.toISOString(), // TODO: do we convert to ISO or UTC string?
        });
      }
    }, [loan?._id, dateRange]);

    /** Render **/
    return (
      <Box sx={{ overflowY: 'auto' }} height={listHeight}>
        {loan && listData && (
          <>
            {showDatePicker && <DatePicker />}

            {showHeader && (
              <ListHeader
                items={[
                  { flex: 1, propName: '__t', label: 'Type' },
                  { flex: 1, propName: 'effectiveDate', label: 'Effective Date' },
                  { flex: 1, propName: 'advanceAmount', label: 'Advance Amount' },
                  { flex: 1, propName: 'feeTotal', label: 'Fees Charged' },
                  { flex: 1, propName: 'paymentAmount', label: 'Payment Amount' },
                  { flex: 1, propName: 'appliedToFees', label: 'Fees Paid' },
                  { flex: 1, propName: 'appliedToInterest', label: 'Interest Paid' },
                  { flex: 1, propName: 'appliedToPrincipal', label: 'Principal Paid' },
                  { flex: 1, propName: 'newPrincipalBalance', label: 'Principal Balance' },
                ]}
                sortType={sortType}
                sortReverse={sortReverse}
                sortList={sortList}
              />
            )}

            <Box>
              {listData.map((transaction, i) => (
                <ListRow
                  key={transaction._id + '-' + i}
                  rowHeight={rowHeight}
                  items={[
                    {
                      propName: '__t',
                      content: (
                        <>
                          {transaction.__t === DataModelPluralType.Advance && (
                            <Box component="span" display="flex">
                              <Tooltip title={capitalize(transaction.__t)}>
                                {dataModelIcons[DataModelType.Advance]}
                              </Tooltip>
                            </Box>
                          )}
                          {transaction.__t === DataModelPluralType.Payment && (
                            <Box component="span" display="flex">
                              <Tooltip title={capitalize(transaction.__t)}>
                                {dataModelIcons[DataModelType.Payment]}
                              </Tooltip>
                            </Box>
                          )}
                          {transaction.__t === DataModelPluralType.Statement && (
                            <Box component="span" display="flex">
                              <Tooltip title={capitalize(transaction.__t)}>
                                {dataModelIcons[DataModelType.Statement]}
                              </Tooltip>
                            </Box>
                          )}
                        </>
                      ),
                    },
                    { propName: 'effectiveDate', content: dayjs(transaction.effectiveDate).format(mediumDate) },
                    {
                      propName: 'advanceAmount',
                      content: transaction.advanceAmount ? currency.format(transaction.advanceAmount) : '',
                      url: `/${transaction.__t}/${transaction._id}`,
                    },
                    {
                      propName: 'feeTotal',
                      content:
                        transaction.__t !== DataModelPluralType.Statement
                          ? currency.format(transaction.feeTotal ?? 0)
                          : '',
                      url: `/${transaction.__t}/${transaction._id}`,
                    },
                    {
                      propName: 'paymentAmount',
                      content: transaction.paymentAmount ? currency.format(transaction.paymentAmount) : '',
                      url: `/${transaction.__t}/${transaction._id}`,
                    },
                    {
                      propName: 'appliedToFees',
                      content: transaction?.appliedToFees ? currency.format(transaction?.appliedToFees) : '',
                    },
                    {
                      propName: 'appliedToInterest',
                      content: transaction?.appliedToInterest ? currency.format(transaction?.appliedToInterest) : '',
                    },
                    {
                      propName: 'appliedToPrincipal',
                      content: transaction?.appliedToPrincipal ? currency.format(transaction?.appliedToPrincipal) : '',
                    },
                    {
                      propName: 'newPrincipalBalance',
                      content: transaction?.newPrincipalBalance
                        ? currency.format(transaction?.newPrincipalBalance)
                        : '',
                    },
                  ]}
                  rowIndex={i}
                />
              ))}
            </Box>
          </>
        )}

        {loan && !!transactions?.length && (
          <ListRow
            sx={{ fontWeight: 'bold' }}
            rowHeight={rowHeight}
            items={[
              {
                flex: 1,
                propName: '',
                content: (
                  <Box component="span" display="flex">
                    Totals
                  </Box>
                ),
              },
              { flex: 1, propName: '', content: '' },
              {
                flex: 1,
                propName: 'totals.advanceAmount',
                content: currency.format(totals?.advanceAmount ?? 0),
              },
              {
                flex: 1,
                propName: 'totals.feesCharged',
                content: currency.format(totals?.feesCharged ?? 0),
              },
              {
                flex: 1,
                propName: 'totals.paymentAmount',
                content: currency.format(totals?.paymentAmount ?? 0),
              },
              {
                flex: 1,
                propName: 'totals.feesPaid',
                content: currency.format(totals?.feesPaid ?? 0),
              },
              {
                flex: 1,
                propName: 'totals.interestPaid',
                content: currency.format(totals?.interestPaid ?? 0),
              },
              {
                flex: 1,
                propName: 'totals.principalPaid',
                content: currency.format(totals?.principalPaid ?? 0),
              },
              {
                flex: 1,
                propName: '',
                content: '',
              },
            ]}
            rowIndex={transactions.length}
          />
        )}

        {!loan && !hideErrors && (
          <Box display="flex" layout-align="center center" className="md-headline thin pad-top-bottom">
            Please Select a Deal Above.
          </Box>
        )}

        {loan && transactions && !transactions.length && !hideErrors && (
          <Box display="flex" layout-align="center center" className="md-title thin pad-top-bottom">
            No Transactions Found.
          </Box>
        )}
      </Box>
    );
  }
);
